const AbstractLine = require('@/lib/Lines/AbstractLine');
const BigNumber = require('bignumber.js');
const BookingGroup = require('@/lib/GroupBookings/BookingGroup');
const Branch = require('@/lib/Branches/Branch');
const CartItemDiscount = require('@/libraries/DigiTickets/Offers/CartItemDiscount');
const Category = require('@/lib/Categories/Category');
const Contact = require('@/lib/Contacts/Contact');
const ItemInstance = require('./ItemInstance');
const ItemType = require('@/lib/Items/ItemType');
const MembershipPlan = require('@/lib/Memberships/MembershipPlan');
const Offer = require('@/lib/Offers/Offer');
const ReturnReason = require('@/lib/ReturnReasons/ReturnReason');
const Session = require('@/lib/Sessions/Session');
const SessionEvent = require('@/lib/Sessions/SessionEvent');
const SoldGiftVoucherData = require('@/lib/GiftVouchers/SoldGiftVoucherData');
const Ticket = require('@/lib/Tickets/Ticket');
const { hydrate } = require('@/lib/Hydration');
const { isSameDay } = require('@/functions/date');
const { populate } = require('@/functions/objects');
const { toBool, toDate, toFloat, toInt, toNullableInt, toNullableString, toString } = require('@/functions/transform');

class OrderLine extends AbstractLine {
    /**
     * @param {object} [data]
     *
     * @constructor
     */
    constructor(data = {}) {
        super();

        /**
         * Simple name/values
         */
        this.ID = null;

        /**
         * Branch ID this item belongs to. (Will not be null).
         *
         * @type {number}
         */
        this.branchID = undefined;

        /**
         * If the user tries to redeem a line that is from another branch we will display
         * a confirmation. Once that confirmation has been accepted we don't want to bug
         * them every time, so this flag gets set.
         *
         * @type {boolean}
         */
        this.crossBranchRedemptionConfirmed = false;

        /**
         * Raw field data from the API
         */
        this.adjustedByID = null;
        this.adjustsID = null;
        this.donation = 0;

        /**
         * Is this line expired?
         * Provided by the API, based on this line existing in the orders_items_expired table.
         * Note that this will be false for expired open tickets! (see PRO-805)
         *
         * @type {boolean}
         */
        this.expired = false;

        this.giftAid = null;

        /**
         * @type {Number}
         */
        this.itemID = null;

        this.itemType = null;
        this.lastRedeemedAt = null;
        this.name = null;
        this.orderID = null;
        this.people = undefined;

        /**
         * @deprecated
         * Note this is for V1 Product Options, not V2 Item Options.
         *
         * If this line came directly from a cart line this will be set correctly
         * as the sum of selected product options.
         * If this line came from the API we cannot safely recalculate the cost of the options,
         * so this will remain null and should not be used.
         *
         * @type {number|null}
         */
        this.priceOfOptions = null;

        this.qty = 0;

        /**
         * @type {boolean}
         */
        this.redeemable = false;

        this.redeemed = 0;
        this.returnToStock = false;
        this.subtitle = null;
        this.surchargeID = null;
        this.validityPeriod = null;

        /**
         * When this open ticket expires, if it has a validityPeriod.
         * See calculateOrderLineValidUntil function.
         *
         * @type {?Date}
         */
        this.validUntil = null;

        /**
         * Objects
         */
        this.category = null;

        /**
         * @type {?CartItemDiscount}
         */
        this.discount = null;

        /**
         * @type {?number}
         */
        this.eventID = null;

        /**
         * @type {?SessionEvent}
         */
        this.event = null;

        /**
         * @type {?ReturnReason}
         */
        this.returnReason = null;

        /**
         * @type {?Number}
         */
        this.sessionID = null;

        /**
         * @type {?SoldGiftVoucherData}
         */
        this.soldGiftVoucherData = null;

        /**
         * Array of instances on this line.
         * Note that the 'instance' numbers on the objects within this array begin with 1 not 0.
         * (The array itself still starts at zero.)
         *
         * Take care with the case of this property name! It is all lowercase, not camelCase.
         *
         * @type {ItemInstance[]}
         */
        this.iteminstances = [];

        /**
         * @type {LineTaxSummary}
         */
        this.taxSummary = null;

        // Information about whether a redemption request is in progress or not.
        this.redemptionInProgress = false;
        this.unredemptionInProgress = false;
        this.redemptionError = false;
        this.currentRedeemed = null;

        /**
         * Synthetic line number, generated in Order.afterHydration.
         * Index of the line in the order, starting from 1.
         *
         * @type {number}
         */
        this.lineNumber = 0;

        this.absQty = 0;

        // Represents the option in the UI as to whether the custom fields are currently visible
        this.customFieldsVisible = false;

        /**
         * When the order is loading on the OrderDetails page each item is checked
         * to see if the relevant ticket requires a waiver (if it is a ticket line).
         * This is based on the 'hasWaiver' flag of the ticket at the last time tickets were synced.
         * There is a low chance but it is possible for it to be out of sync with the actual
         * waivers generated for the order. Therefore this flag is only used for offline support
         * of waivers. While online you should refer to the OrderWaiverStatus object loaded
         * alongside the order.
         *
         * @type {boolean|null}
         */
        this.ticketHasWaiver = null;

        /**
         * This is populated in afterHydration to save too much recalculated cause by Angular.
         *
         * @type {string[]}
         */
        this.seatNumberStrings = [];

        /**
         * If the user has tried to redeem this line, and there are unallocated seats on it,
         * they will be presented with a confirmation dialog. If they confirm, this flag will be set.
         * This is used to prevent the dialog from appearing again.
         *
         * @type {boolean}
         */
        this.unallocatedSeatRedemptionConfirmed = false;

        /**
         * Used for group bookings.
         *
         * @type {BookingGroup|null}
         */
        this.bookingGroup = null;

        /**
         * If this is a child line this is the ID of the parent line.
         *
         * @type {number|null}
         */
        this.parentID = null;

        /**
         * If this line was created as a response to an [item] option set, this is the ID of that option set.
         *
         * @type {number|null}
         */
        this.optionSetID = null;

        /**
         * This does not come from the API. It is only set when we convert a cart to an order within ProPoint.
         *
         * @type {string|null}
         */
        this.thirdPartyRef = null;

        /**
         * This does not come from the API. It is only set when we convert a cart to an order within ProPoint.
         *
         * @type {string|null}
         */
        this.parentThirdPartyRef = null;

        populate(this, data);
    }

    getName() {
        if (this.item) {
            if (this.giftAid) {
                return this.item.getName() + ' (Gift Aid)';
            }
            return this.item.getName();
        }
        return this.name;
    }

    isAdjustment() {
        return !!this.adjustsID;
    }

    isAdjusted() {
        return !!this.adjustedByID;
    }

    /**
     * @param {SoldGiftVoucher} soldGiftVoucher
     */
    assignSoldGiftVoucher(soldGiftVoucher) {
        this.soldGiftVoucherData = Object.assign(new SoldGiftVoucherData(), {
            deliveryType: soldGiftVoucher.deliveryType,
            recipientContact: Object.assign(new Contact(), soldGiftVoucher.recipient),
            recipientMessage: soldGiftVoucher.recipientMessage,
        });
    }

    /**
     * Find out whether redemptions are allowed on this line.
     * Only tickets can be redeemed.
     *
     * Current expired tickets can no longer be redeemed, but we could add a flag to BO
     * and allow expired tickets to be redeemable depending on user preference.
     */
    isRedemptionAllowed() {
        return this.itemType === 'Ticket'
            && !(this.isAdjustment() || this.isAdjusted())
            && this.redeemable === true
            && !this.isExpired();
    }

    /**
     * @return {boolean}
     */
    isExpired() {
        return this.expired || this.validUntilHasPassed();
    }

    /**
     * @return {boolean}
     */
    validUntilHasPassed() {
        return this.validUntil !== null && this.validUntil < new Date();
    }

    /**
     * Returns the CSS class to use on the redemption button for this line.
     * - red/danger = already fully redeemed
     * - orange/warning = redeemable, but the session is not today
     * - green/success = redeemable, and the session is today
     *
     * @param {number} currentBranchID
     * @param {boolean} skipRedemptionCheck - A workaround for a bug with individual instance tickets.
     * When you (un)redeem these the redemption count on the line does not seem to update so you can end up
     * with an incorrect red + button. Set this to true to ignore the redeemed qty and never return red because that
     * is handled elsewhere in the template.
     *
     * @return {string}
     */
    getRedeemButtonColorClass(currentBranchID, skipRedemptionCheck = false) {
        if (!skipRedemptionCheck && this.redeemed === this.qty) {
            return 'btn-danger';
        }

        // This is not implemented because the (un)redeem group button was hard to make the same change to,
        // and it's best to be consistent. Maybe we can revisit this in future.
        // if (this.branchID !== currentBranchID) {
        //     // Line is from another branch.
        //     return 'btn-warning';
        // }

        if (this.session) {
            if (!isSameDay(this.session.startTime, new Date())) {
                return 'btn-warning';
            }
        }

        return 'btn-success';
    }

    toggleCustomFieldsVisibility() {
        this.customFieldsVisible = !this.customFieldsVisible;
    }

    getRedemptionCount() {
        return this.redeemed;
    }

    getItemType() {
        return this.itemType;
    }

    /**
     * @param {string} itemType
     *
     * @return {OrderLine}
     */
    setItemType(itemType) {
        this.itemType = itemType;

        return this;
    }

    getHydrationMap() {
        return {
            adjustedByID: toNullableInt,
            adjustsID: toNullableInt,
            baseTotal: toFloat,
            bookingFee: toFloat,
            bookingFeeSubtotal: toFloat,
            bookingGroup: {
                field: ['bookingGroup', 'bookinggroups'],
                model: BookingGroup,
            },
            branch: {
                field: ['branch', 'branches'],
                model: Branch,
            },
            branchID: toInt,
            category: {
                field: ['category', 'categories'],
                model: Category,
            },
            discountAmount: toFloat,
            donation: toFloat,
            expired: toBool,
            event: {
                field: ['event', 'events'],
                model: SessionEvent,
            },
            eventID: {
                field: ['eventCatID', 'eventID'],
                transform: toNullableInt,
            },
            giftAid: toBool,
            ID: {
                field: ['ID', 'ordersItemsID'],
                transform: toInt,
            },
            item: {
                field: ['item', 'items'],
                modelFunc(data) {
                    const mapItemTypeToModel = require('@/lib/Items/mapItemTypeToModel');

                    if (data.item && data.item.itemType) {
                        return mapItemTypeToModel(data.item.itemType);
                    }
                    if (data.items && data.items.itemType) {
                        return mapItemTypeToModel(data.items.itemType);
                    }
                    if (data.itemType) {
                        return mapItemTypeToModel(data.itemType);
                    }
                    return null;
                },
            },
            itemID: toInt,
            iteminstances: {
                modelCollection: ItemInstance,
            },
            itemType: toString,
            lastRedeemedAt: toDate,
            lineTotal: toFloat,
            name: toNullableString,
            optionSetID: toNullableInt,
            orderID: toInt,
            parentID: toNullableInt,
            price: toFloat,
            qty: toInt,
            redeemable: toBool,
            redeemed: toInt,
            returnReason: {
                field: ['returnReason', 'returnreasons'],
                model: ReturnReason,
            },
            returnToStock: toBool,
            session: {
                field: ['session', 'sessions'],
                model: Session,
            },
            sessionID: toNullableInt,
            surchargeID: toNullableInt,
            soldGiftVoucherData: {
                model: SoldGiftVoucherData,
            },
            validityPeriod: toNullableInt,
        };
    }

    afterHydration(data) {
        if (data.discountAmount) {
            let discount = new CartItemDiscount();
            discount.qty = this.qty;
            discount.amount = data.discountAmount;
            discount.percentage = data.discountPercentage;

            // data.offers may be an empty array even if there is a discountAmount.
            // This can happen when using the discount field while selling in the Back Office.
            discount.offer = data.offers?.length > 0 ? hydrate(data.offers[0], new Offer()) : null;

            if (discount.offer && discount.offer.trigger === 'coupon') {
                discount.offer.code = data?.ordersitemsoffers?.offerscodes?.code;
            }
            discount.offerID = discount.offer ? discount.offer.ID : null;
            this.discount = discount;
        }

        if (this.hasSoldGiftVoucher()) {
            this.assignSoldGiftVoucher(this.iteminstances[0].soldGiftVoucher);
        }

        this.absQty = Math.abs(this.qty);

        // Extract the seat numbers from the item instances.
        this.seatNumberStrings = this.iteminstances.reduce((result, itemInstance) => {
            return result.concat(itemInstance.seatNumbers.map((seatNumber) => seatNumber.seatNumber));
        }, []);
    }

    getEvent() {
        return this.event;
    }

    getEventID() {
        return this.event ? this.event.ID : this.eventID;
    }

    /**
     * @param {AbstractItem} item
     *
     * @return {OrderLine}
     */
    setItem(item) {
        this.item = item;
        this.setItemType(item.itemType);

        return this;
    }

    getItemID() {
        return this.item ? this.item.ID : this.itemID;
    }

    getSessionID() {
        return this.session ? this.session.ID : this.sessionID;
    }

    getQuantity() {
        return this.qty;
    }

    getDonation() {
        return this.donation;
    }

    setRedemptionInProgress() {
        this.redemptionInProgress = true;
        this.unredemptionInProgress = false;
    }

    setUnredemptionInProgress() {
        this.unredemptionInProgress = true;
        this.redemptionInProgress = false;
    }

    clearRedemptionInProgress() {
        this.redemptionInProgress = false;
        this.unredemptionInProgress = false;
    }

    /**
     * @param {boolean} redemptionError
     */
    setRedemptionError(redemptionError) {
        this.clearRedemptionInProgress();
        this.redemptionError = redemptionError;
    }

    redemptionIsInProgress() {
        return this.redemptionInProgress || this.unredemptionInProgress;
    }

    /**
     * If this is true redemption is handled differently.
     * This causes each ticket (each instance on the line) to be handled individually. We cannot just redeem some qty
     * of the line - it now matters *which* instance you are redeeming.
     *
     * @returns {boolean}
     */
    hasIndividualTicketInstances() {
        return !!(this.itemType === ItemType.TICKET && this.item.printTicketPerInstance);
    }

    /**
     * @return {boolean}
     */
    hasSoldGiftVoucher() {
        return !!(this.iteminstances && this.iteminstances.find((i) => i.soldGiftVoucher));
    }

    summaryItemInstanceNetRedemptions() {
        let summary = 0;
        if (this.hasIndividualTicketInstances()) {
            this.iteminstances.forEach(function (itemInstance) {
                summary += parseInt(itemInstance.netRedemptions, 10);
            });
        }
        return summary;
    }

    setCurrentRedeemed(force) {
        if (this.currentRedeemed == null || !!force) {
            this.currentRedeemed = this.redeemed;
        }
    }

    getCurrentRedeemed() {
        return this.currentRedeemed;
    }

    /**
     * This is for backward compatibility because this function is called in receipt templates, stored in the app
     * repository.
     * The expiry date is now calculated externally and set as the this.validUntil property.
     *
     * @returns {?Date}
     */
    getExpiryDate() {
        return this.validUntil;
    }

    /**
     * @returns {Membership[]}
     */
    getMemberships() {
        return this.iteminstances.map((ii) => ii.getMembership()).filter((m) => !!m);
    }

    /**
     * Get a list of Members on this order line.
     *
     * @return {Member[]}
     */
    getMembers() {
        let orderLineMembers = [];

        this.iteminstances.forEach(function (itemInstance) {
            orderLineMembers = [].concat(orderLineMembers, itemInstance.getMembers());
        });

        return orderLineMembers;
    }

    /**
     * @return {boolean}
     */
    isMembershipPlan() {
        return this.itemType === ItemType.MEMBERSHIP_PLAN;
    }

    isTicket() {
        return this.itemType === ItemType.TICKET;
    }

    /**
     * @param {number} instanceNumber
     *
     * @return {?ItemInstance}
     */
    getInstance(instanceNumber) {
        return this.iteminstances.find((i) => i.instance === instanceNumber) || null;
    }

    /**
     * Returns true if the line contains any instances that contain any non-empty field data.
     *
     * @return {boolean}
     */
    hasAnyFieldData() {
        for (let i = 0; i < this.iteminstances.length; i++) {
            if (this.iteminstances[i].fields.hasInstancesWithValue()) {
                return true;
            }
        }

        return false;
    }

    /**
     * For compatibility with CartLine.
     *
     * @return {ItemInstance[]}
     */
    getItemInstances() {
        return this.iteminstances;
    }

    /**
     * The price to use in the price column for this line on the receipt.
     * The differences from this.price are:
     * - Any donation is deducted from the price as it is shown on a separate line.
     *
     * Note that these are still included on the lineTotal printed in the last column.
     *
     * @return {number}
     */
    get priceForReceipt() {
        return new BigNumber(this.price)
            .minus(this.donation)
            .toNumber();
    }

    get hasPriceOfOptions() {
        return this.priceOfOptions !== null;
    }

    /**
     * Normalise with CartLine.
     *
     * @return {number}
     */
    get quantity() {
        return this.qty;
    }

    /**
     * Normalise with CartLine.
     *
     * @return {ItemInstance[]}
     */
    get itemInstances() {
        return this.iteminstances;
    }

    /**
     * The price to use in the price column for this line on the receipt.
     * The differences from this.price are:
     * - Any donation is deducted from the price as it is shown on a separate line.
     * - The price of any chargeable product options are deducted from the price
     *
     * Note that these are still included on the lineTotal printed in the last column.
     *
     * @return {number}
     */
    get priceForReceiptExcludingOptions() {
        return new BigNumber(this.price)
            .minus(this.donation)
            .minus(this.priceOfOptions || 0)
            .toNumber();
    }

    /**
     * @param {string} type
     *
     * @return {string}
     */
    getTaxRateLetter(type) {
        if (!this.taxSummary?.taxSummaryGroups) {
            return '';
        }

        return Array.from(new Set(
            this.taxSummary.taxSummaryGroups
                .filter((taxSummaryGroup) => taxSummaryGroup.type === type)
                .map((taxSummaryGroup) => taxSummaryGroup.letter)
        )).join('');
    }

    /**
     * @returns {string[]}
     */
    getSeatNumbersAsStrings() {
        const seatNumberStrings = [];

        this.iteminstances.forEach((itemInstance) => {
            itemInstance.seatNumbers.forEach((seatNumber) => {
                seatNumberStrings.push(seatNumber.seatNumber);
            });
        });

        return seatNumberStrings;
    }

    /**
     * Return the total number of people on this line.
     *
     * @returns {number|null}
     */
    getPeople() {
        if (this.people !== undefined) {
            return this.people;
        }

        if (
            this.item instanceof MembershipPlan
            || this.item instanceof Ticket
        ) {
            return new BigNumber(this.getQuantity())
                .multipliedBy(this.item.getPeople())
                .toNumber();
        }

        return null;
    }

    /**
     * If this line is for a ticket that requires seat allocation, this function will the number of
     * *people* on the line that do not have a seat allocated.
     *
     * @returns {number|null}
     */
    get unallocatedSeatCount() {
        if (!(this.item instanceof Ticket && this.item.hasSeats)) {
            // If this is not a ticket with seats return null.
            return null;
        }

        return new BigNumber(this.getPeople())
            .minus(this.seatNumberStrings.length)
            .toNumber();
    }
}

module.exports = OrderLine;
